import translator from "@/translator";
import React, { useState, useEffect } from "react";
import BackButton from "@/note_office/shared/BackButton";
import { List, message, Spin, Space, Checkbox, Button } from "antd";
import CustomPagination from "@/note_office/shared/CustomPagination";
import axios from "axios";
import DropdownMenu from "./components/DropdownMenu";
import Helper from "@/helper";
import useBulkUtils from "@/note_office/hooks/useBulkUtils";

const t = translator("note_office.views.services.index_view.");

const IndexView = ({ servicesData }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const mode = searchParams.get("mode");
  const { cancelBulkMode, toggleBulkSelected } = useBulkUtils();
  const csrfToken = Helper.getCSRFToken();

  const {
    bookings_menu_url,
    services_url,
    new_service_url,
    services_delete_url,
    services_copy_url,
    total_services: initialTotalServices,
    services: initialServices,
    total_pages: initialTotalPages,
    current_page: initialCurrentPage,
    per_page: initialPerPage,
  } = servicesData || {};

  const [services, setServices] = useState(initialServices || []);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialCurrentPage || 1);
  const [perPage, setPerPage] = useState(initialPerPage || 8);
  const [totalServices, setTotalServices] = useState(initialTotalServices || 0);
  const [totalPages, setTotalPages] = useState(initialTotalPages || 1);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const isSelected = (id) => selectedIds.has(id);
  const handleToggle = (id) => setSelectedIds(toggleBulkSelected(selectedIds, id));
  const handleCancel = cancelBulkMode;

  const fetchServices = async (page) => {
    setLoading(true);
    try {
      const url = `${services_url}?page=${page}&per_page=${perPage}`;
      const response = await axios.get(url);
      const data = response.data;
      setServices(data.services || []);
      setTotalServices(data.total_services);
      setTotalPages(data.total_pages);
      setCurrentPage(data.current_page);
      setPerPage(data.per_page);
    } catch (error) {
      message.error(t("server_error"));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices(currentPage);
  }, [currentPage]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [mode]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleBulkAction = async ({ url, method, confirmMessage, successMessage, data }) => {
    if (!confirm(t(confirmMessage))) return;
    try {
      const response = await axios({
        method,
        url,
        data,
        headers: {
          "X-CSRF-Token": csrfToken,
          "Content-Type": "application/json",
        },
      });

      if (response.data.count) {
        const count = response.data.count;
        message.success(t(successMessage, { count }));
      }

      if (response.data.errors) {
        message.error(response.data.errors);
      }

      setSelectedIds(new Set());
      const result = await axios.get(services_url, {
        params: { page: 1, perPage },
      });

      setServices(result.data.services || []);
      setTotalServices(result.data.total_services);
      setTotalPages(result.data.total_pages);
      setCurrentPage(result.data.current_page);
      setPerPage(result.data.per_page);

      if (result.data.total_services === 0) {
        handleCancel();
      }
    } catch (e) {
      message.error(e);
      console.error(e);
    }
  };

  const onDelete = () =>
    handleBulkAction({
      url: services_delete_url,
      method: "delete",
      confirmMessage: "bulk_actions.delete_confirm",
      successMessage: "bulk_actions.delete_success",
      data: { ids: Array.from(selectedIds) },
    });

  const onCopy = () =>
    handleBulkAction({
      url: services_copy_url,
      method: "post",
      confirmMessage: "bulk_actions.copy_confirm",
      successMessage: "bulk_actions.copy_success",
      data: { ids: Array.from(selectedIds) },
    });

  const formatDuration = (duration) => {
    const minutesTotal = duration / 60;
    const hours = Math.floor(minutesTotal / 60);
    const minutes = minutesTotal % 60;

    if (hours > 0 && minutes > 0) {
      return t("duration.hours_minutes", { hours, minutes });
    }
    if (hours > 0) {
      return t("duration.hours", { hours });
    }
    return t("duration.minutes", { minutes });
  };

  return (
    <div className="wrapper wrapper__menu-layout overflow">
      <BackButton link={bookings_menu_url} text={t("title")}>
        <Space>
          <DropdownMenu totalServices={totalServices} />
        </Space>
      </BackButton>

      <a href={new_service_url} className="services__new-button">
        <span className="services__plus">+</span>
        <span className="services__text-button">{t("new_service")}</span>
      </a>

      <div className="bulk_deliveries__content overflow">
        <div className="contacts-index__list custom-list">
          <List
            itemLayout="horizontal"
            dataSource={services}
            renderItem={(service) => (
              <List.Item
                key={service.id}
                onClick={() => {
                  if (mode === "delete" || mode === "copy") {
                    handleToggle(service.id);
                  } else {
                    window.location.href = service.edit_url;
                  }
                }}
                className={[
                  mode === "delete" || mode === "copy" ? "selectable-item" : "",
                  !service.active ? "services-inactive" : "",
                ].join(" ")}
                extra={
                  <div className="services-price-block">
                    {service.price_min ? (
                      <>
                        <div className="services-min-price">{`${service.currency_symbol} ${service.price_min}`}</div>
                        {service.price_max && (
                          <div className="services-max-price">{`${service.currency_symbol} ${service.price_max}`}</div>
                        )}
                      </>
                    ) : (
                      <div className="services-min-price">{`${service.currency_symbol} ${service.price_max}`}</div>
                    )}
                  </div>
                }
              >
                <List.Item.Meta
                  avatar={
                    (mode === "delete" || mode === "copy") && (
                      <Checkbox
                        checked={isSelected(service.id)}
                        onChange={() => handleToggle(service.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )
                  }
                  title={<div className="services-index__name">{service.name}</div>}
                  description={
                    <div className="services-index__duration">
                      {formatDuration(service.duration)}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </div>

        {loading && <Spin size="large" className="loader" />}

        {totalPages > 1 && (
          <CustomPagination
            current={currentPage}
            total={totalServices}
            pageSize={perPage}
            onChange={handlePageChange}
          />
        )}
        {["delete", "copy"].includes(mode) && (
          <Space direction="vertical" size={16} className="button-group">
            <Button
              type="primary"
              className="button-base"
              block
              disabled={selectedIds.size === 0}
              onClick={mode === "delete" ? onDelete : onCopy}
            >
              {t(`bulk_actions.buttons.${mode}`)}
            </Button>
            <Button
              type="default"
              block
              onClick={handleCancel}
              className="button-base button-secondary"
            >
              {t("bulk_actions.buttons.cancel")}
            </Button>
          </Space>
        )}
      </div>
    </div>
  );
};

export default IndexView;
